import { useState, useMemo } from 'react'
import { Link as ExternalLink } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

import QRCode from "react-qr-code"
import { CopyToClipboard } from 'react-copy-to-clipboard'

import DataExportDialog from '/components/data-export-dialog'
import excelExport from './download-results-to-excel.js'

import { Box, Button, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import SaveAltIcon from '@mui/icons-material/SaveAlt'

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'
import { createTableStyles } from '../table-styles'
import StyledPaper from '../paper.jsx'

const ResultsGrid = ({ template, passesToProcess, makePassResults, setMakePassResults, setProcessing }) => {

  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])

  const [selectedRow, setSelectedRow] = useState(null)
  const [appleCopied, setAppleCopied] = useState(false)
  const [googleCopied, setGoogleCopied] = useState(false)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const closeResults = () => {
    setMakePassResults([])
    setProcessing(false)
  }

  const showPassLinks = data => {
    setSelectedRow(data)
    setAppleCopied(false)
    setGoogleCopied(false)
  }

  const processing = useMemo(() => makePassResults.length !== passesToProcess, [makePassResults])

  const columns = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'Pass ID',
      filterVariant: 'autocomplete',
      size: 300,
      Cell: ({ cell, row }) => <div className='link' onClick={() => showPassLinks(row.original)}><OpenInNewIcon className='mr-5 grey' /> {cell.getValue()}</div>,
    },
    {
      accessorKey: 'appleUrl',
      header: 'Apple Install URL',
      filterVariant: 'autocomplete',
      size: 600,
      enableClickToCopy: true,
      Cell: ({ cell }) => <><ContentCopyOutlinedIcon className='mr-5 grey' /> {cell.getValue()}</>,
    },
    {
      accessorKey: 'googleUrl',
      header: 'Google Android Install URL',
      filterVariant: 'autocomplete',
      size: 600,
      enableClickToCopy: true,
      Cell: ({ cell }) => <><ContentCopyOutlinedIcon className='mr-5 grey' /> {cell.getValue()}</>,
    },
    {
      accessorKey: 'error',
      header: 'Error',
      filterVariant: 'autocomplete',
      size: 600,
    },
  ])

  const table = useMaterialReactTable({
    columns,
    data: makePassResults,
    state: {
      isLoading: processing
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id,
    ...tableStyles,
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Button variant='contained' onClick={closeResults}>Close</Button>
    )

  })

  return (
    <>
      <Box sx={{ height: 'calc(100vh - 185px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
          <MaterialReactTable table={table} />
        </StyledPaper>
      </Box>

      {selectedRow !== null &&
        <Dialog
          open={selectedRow !== null}
          onClose={() => setSelectedRow(null)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          slotProps={{
            paper: {
              sx: {
                width: '700px',
                maxWidth: '700px',
              },
            },
          }}
        >
          <DialogContent>
            <div className='flex flex-row gap-5 justify-between'>

              <div className='flex flex-col gap-10 items-center '>
                <div><ExternalLink to={selectedRow?.appleUrl} target='_pass'><img src={appleButton} width={300} /></ExternalLink></div>
                <div className='bg-white p-5'>
                  <QRCode value={selectedRow?.appleUrl} />
                </div>
                <div>
                  <CopyToClipboard text={selectedRow?.appleUrl} onCopy={() => setAppleCopied(true)}>
                    <button className='link'><ContentCopyOutlinedIcon color='primary' />{appleCopied ? "Copied" : "Copy URL to clipboard"}</button>
                  </CopyToClipboard>
                </div>
              </div>

              <div className='flex flex-col gap-10 items-center'>
                <div><ExternalLink to={selectedRow?.googleUrl} target='_pass'><img src={googleButton} width={300} /></ExternalLink></div>
                <div className='bg-white p-5'>
                  <QRCode value={selectedRow?.googleUrl} />
                </div>
                <div>
                  <CopyToClipboard text={selectedRow?.googleUrl} onCopy={() => setGoogleCopied(true)}>
                    <button className='link'><ContentCopyOutlinedIcon color='primary' />{googleCopied ? "Copied" : "Copy URL to clipboard"}</button>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex flex-row w-full p-5">
              <Button variant="outlined" onClick={() => setSelectedRow(null)}>Close</Button>
            </div>
          </DialogActions>
        </Dialog>
      }

      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${template.id}-new-passes`} data={makePassResults} excelExport={excelExport} />

    </>
  )
}

export default ResultsGrid